import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  getMessages,
  deleteMessage,
  markAsRead,
} from "../../api/contactApi";

const formatPersianDateTime = (value) => {
  if (!value) return "بدون زمان";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "بدون زمان";

  return date.toLocaleString("fa-IR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeMessages = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.messages)) return data.messages;
  if (Array.isArray(data?.data)) return data.data;

  return [];
};

export default function ContactMessagesPage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [error, setError] = useState("");

  const token = localStorage.getItem("staffToken");

  const unreadCount = useMemo(() => {
    return messages.filter((msg) => !msg.isRead).length;
  }, [messages]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError("");

      if (!token) {
        setError("دسترسی غیرمجاز است. لطفاً دوباره وارد پنل شوید.");
        setMessages([]);
        return;
      }

      const data = await getMessages(token);
      setMessages(normalizeMessages(data));
    } catch (error) {
      console.error("Fetch contact messages error:", error);
      setError(error.message || "خطا در دریافت پیام‌های تماس");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    const confirmed = window.confirm("آیا از حذف این پیام مطمئن هستید؟");

    if (!confirmed) return;

    try {
      setActionLoadingId(id);
      setError("");

      await deleteMessage(id, token);

      setMessages((prev) => prev.filter((msg) => msg._id !== id));
    } catch (error) {
      console.error("Delete contact message error:", error);
      setError(error.message || "خطا در حذف پیام");
    } finally {
      setActionLoadingId("");
    }
  };

  const handleRead = async (id) => {
    try {
      setActionLoadingId(id);
      setError("");

      const updated = await markAsRead(id, token);

      setMessages((prev) =>
        prev.map((msg) => (msg._id === id ? { ...msg, ...updated } : msg))
      );
    } catch (error) {
      console.error("Mark contact message as read error:", error);
      setError(error.message || "خطا در تغییر وضعیت پیام");
    } finally {
      setActionLoadingId("");
    }
  };

  return (
    <main className="admin-main admin-contact-messages-page">
      <section className="admin-contact-header">
        <div>
          <span className="admin-badge">پیام‌های کاربران</span>
          <h1>پیام‌های تماس</h1>
          <p>
            پیام‌های ارسال‌شده از فرم تماس سایت را مشاهده، مدیریت و پیگیری کنید.
          </p>
        </div>

        <div className="admin-contact-header-actions">
          <div className="admin-contact-count-card">
            <span>پیام‌های جدید</span>
            <strong>{new Intl.NumberFormat("fa-IR").format(unreadCount)}</strong>
          </div>

          <button
            type="button"
            className="admin-secondary-btn admin-contact-refresh"
            onClick={fetchMessages}
            disabled={loading}
          >
            {loading ? "در حال بروزرسانی..." : "بروزرسانی"}
          </button>

          <Link to="/admin/dashboard" className="admin-secondary-btn">
            بازگشت به داشبورد
          </Link>
        </div>
      </section>

      {error && <div className="admin-message error">{error}</div>}

      {loading && (
        <div className="admin-contact-state">
          در حال دریافت پیام‌های تماس...
        </div>
      )}

      {!loading && messages.length === 0 && (
        <div className="admin-contact-empty">
          <div className="admin-contact-empty-icon">📭</div>
          <h2>هنوز پیامی ثبت نشده است</h2>
          <p>وقتی کاربران از فرم تماس سایت پیام بفرستند، اینجا نمایش داده می‌شود.</p>
        </div>
      )}

      {!loading && messages.length > 0 && (
        <section className="messages-grid">
          {messages.map((msg) => {
            const isActionLoading = actionLoadingId === msg._id;

            return (
              <article
                key={msg._id}
                className={`message-card ${!msg.isRead ? "unread" : "read"}`}
              >
                <div className="message-card-top">
                  <div className="message-sender">
                    <div className="message-avatar">
                      {(msg.name || "؟").trim().charAt(0)}
                    </div>

                    <div>
                      <h3>{msg.name || "بدون نام"}</h3>

                      <a
                        href={`mailto:${msg.email || ""}`}
                        className="message-email"
                        dir="ltr"
                      >
                        {msg.email || "بدون ایمیل"}
                      </a>
                    </div>
                  </div>

                  <div className="message-status-wrap">
                    {!msg.isRead ? (
                      <span className="message-badge unread">جدید</span>
                    ) : (
                      <span className="message-badge read">خوانده‌شده</span>
                    )}

                    <time>{formatPersianDateTime(msg.createdAt)}</time>
                  </div>
                </div>

                <div className="message-subject">
                  <span>موضوع</span>
                  <h4>{msg.subject || "بدون موضوع"}</h4>
                </div>

                <p className="message-body">{msg.message || "بدون متن پیام"}</p>

                <div className="message-actions">
                  {!msg.isRead && (
                    <button
                      type="button"
                      className="message-read-btn"
                      onClick={() => handleRead(msg._id)}
                      disabled={isActionLoading}
                    >
                      {isActionLoading ? "در حال ثبت..." : "خوانده شد"}
                    </button>
                  )}

                  <a
                    href={`mailto:${msg.email || ""}?subject=${encodeURIComponent(
                      `پاسخ به پیام: ${msg.subject || ""}`
                    )}`}
                    className="message-reply-btn"
                  >
                    پاسخ ایمیلی
                  </a>

                  <button
                    type="button"
                    className="message-delete-btn"
                    onClick={() => handleDelete(msg._id)}
                    disabled={isActionLoading}
                  >
                    {isActionLoading ? "..." : "حذف"}
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      )}
    </main>
  );
}
